<template>
  <div class="waste-module">
    <b-row class="search-block  mb-3 mt-1">
      <b-col
        xl="4"
        lg="6"
        md="12"
      >
        <searcher-module
          ref="searcherModule"
          name="wasteSearcher"
          :error-message="$tc('waste.error.validIdentifier')"
          sample-search
          :is-general-disabled="true"
          :default-search-type="'currentPage'"
          @sampleSearch="onSampleSearch($event)"
        />
      </b-col>
    </b-row>

    <!--Waste Grid-->
    <div class="grid-container">
      <GridHeader
        :gridtitle="sideBarState.selectedOption !== undefined ? $t(sideBarState.selectedOption.label) : ''"
        :gridsubtitle="msgSelectedRows"
        :qa="componentId"
      >
        <grid-display-options
          v-if="gridApi"
          :ui-component="gridUiComponent"
          @changedPageSize="changedPageSize($event)"
          @changedMode="changedMode($event)"
        />
        <div class="d-flex align-items-center">
          <div
            v-if="_showDiscardButton"
            class="pl-1 pr-0 d-flex align-items-center"
          >
            <b-button
              v-testing="'dispose-of-samples'"
              v-tooltip="$t('waste.grid.contextMenu.disposeOfSample')"
              v-shortkey="['ctrl', 'enter']"
              :disabled="_disabledDiscardButton"
              class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
              @shortkey="disposeOfSamples(selectedRows)"
              @click="disposeOfSamples(selectedRows)"
            >
              <i
                class="far fa-arrow-alt-to-bottom"
                aria-hidden="true"
              />
            </b-button>
            <div class="ml-2 mr-1 vertical-divider" />
          </div>
          <!-- TODO: Front development required for next button -->
          <div
            v-if="false"
            class="pl-1 pr-0 d-flex align-items-center"
          >
            <b-button
              v-tooltip="$t('waste.grid.contextMenu.goToDisposal')"
              qa="waste-sample-grid-go-to-disposal-button"
              class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
            >
              <i
                class="far fa-arrow-alt-from-bottom"
                aria-hidden="true"
              />
            </b-button>
            <div class="ml-2 mr-1 vertical-divider" />
          </div>
          <div
            v-if="_showUndoDiscardButton"
            class="pl-1 pr-0 d-flex align-items-center"
          >
            <b-button
              v-testing="'undo-dispose-of-samples'"
              v-tooltip="$t('waste.grid.contextMenu.undoDisposeOfSample')"
              v-shortkey="['ctrl', 'del']"
              :disabled="_disabledUndoDiscardButton"
              class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
              @shortkey="undoDisposeOfSamples(gridApi.getSelectedRows())"
              @click="undoDisposeOfSamples(gridApi.getSelectedRows())"
            >
              <i
                class="far fa-arrow-alt-from-bottom"
                aria-hidden="true"
              />
            </b-button>
            <div class="ml-2 mr-1 vertical-divider" />
          </div>
          <div class="pl-1 pr-0 d-flex align-items-center">
            <button
              id="samplesCleanFilters"
              v-testing="'clean'"
              v-tooltip="$t('global.cleanFilter')"
              v-shortkey="['ctrl', 'alt', 'f']"
              class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
              type="button"
              @shortkey.prevent="cleanFilters"
              @click="cleanFilters"
            >
              <i
                class="far fa-filter"
                aria-hidden="true"
              />
            </button>
          </div>
          <vt-report-export-buttons
            source="waste-sample-grid"
            :export-report="exportReport"
          />
        </div>
      </GridHeader>
      <vt-grid
        ref="vtGrid"
        :key="`${rowModel}-${selectedOption.id}`"
        v-testing="'elements'"
        class="waste-sample-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-id="componentId"
        :component-code="uiComponent"
        :use-detail-custom-config="true"
      />
    </div>
    <add-custom-filter-modal
      id="addFilterModal"
      ref="addFilterModal"
      @ok="onAddFilterOk"
    />
    <print-component ref="printComponent" />
    <modal-confirmation
      id="optionChangeConfirmationModal"
      ref="optionChangeConfirmationModal"
      :content="$t('component.searcher.optionChangeConfirmation')"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="coreEntityCode"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="reloadRowData()"
      />
    </modal-wrapper>
    <common-attachment-modal-tree
      v-if="selectedPatientForAttachmentTreeModal"
      ref="attachmentTreeModal"
      :patient="selectedPatientForAttachmentTreeModal"
      :laboratory-id="laboratoryId"
      @attachments-updated="reloadRowData()"
    />
    <modal-wrapper ref="incidentModalTreeWrapper">
      <common-incident-modal-tree
        @incident-added="reloadRowData()"
      />
    </modal-wrapper>
    <modal-wrapper ref="incidentFormModal">
      <incident-form-modal
        id="incidentFormModal"
        :core-entity="coreEntityCode"
        @incident-added="reloadRowData()"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import GridHeader from '@/components/GridHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import {
    wasteSampleGridDiscardedColumnDefs,
    wasteSampleGridPreservedColumnDefs,
    wasteSampleGridToBeDiscardedColumnDefs,
    wasteSampleGridToBeUnarchivedForDisposalColumnDefs
} from '@/modules/wasteModule/views/grid/config/waste-sample-grid.column-defs'
import { WASTE_TASK } from '@/modules/wasteModule/sideBar/sideBarOptions'
import { PaginatorLaboratoryDynamicDatasource } from '@/shared/resources/datasources/paginator/paginator-laboratory-dynamic-datasource'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import SearcherModule from '@/components/SearcherModule'
import sideBarStore from '@/modules/wasteModule/sideBar/wasteSideBarStore'
import AddCustomFilterModal from '@/components/customFilter/AddCustomFilterModal'
import customFilterResource from '@/api/resources/ui/CustomFilterResource'
import { isEmpty } from 'lodash'
import Encoder from '@/utils/encoder'
import wasteSampleService from '@/modules/wasteModule/services/WasteSampleService'
import GridDisplayOptions from '@/components/GridDisplayOptions'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree'
import CommonAttachmentModalTree from '@/components/attachments/CommonAttachmentModalTree'
import AddFileModal from '@/components/AddFileModal'
import { COREENTITY_VALUES } from '@/api/resources/incident/incidentServices'
import { GridUtils } from '@/shared/util/grid-utils'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'

const ROW_HEIGHT_NORMAL = 40
const ROW_HEIGHT_COMPACT = 25

export default {
    name: 'WasteSampleGrid',
    components: {
        GridDisplayOptions,
        GridHeader,
        VtGrid,
        SearcherModule,
        ModalConfirmation,
        AddCustomFilterModal,
        IncidentFormModal,
        CommonIncidentModalTree,
        CommonAttachmentModalTree,
        AddFileModal
    },
    mixins: [
        SampleOperationGuardMixin,
        IncidentOperationGuardMixin,
        GridExportMixin
    ],
    props: {
        initMode: { type: Object, default: () => ({ value: 'compact', icon: 'fal fa-align-justify' }) },
        initFilterModel: { type: Object, default: null },
        initSortModel: { type: Array, default: () => [] },
        initPaginator: { type: Object, default: null },
        initTabId: { type: Number, default: 0 }
    },
    data() {
        return {
            mode: this.initMode,
            gridUiComponent: 'uicomponent.waste.samplegrid',
            newClientSideOption: false,
            dataDetail: null,
            messageProcessModal: '',
            samId: null,
            showSidePanel: false,
            columnDefs: [],
            gridApi: null,
            columnApi: null,
            validationRegex: [],
            sideBarState: sideBarStore.state,
            encoder: new Encoder(),
            selectedRows: [],
            searchData: [],
            rowModel: AgGridRowModelType.SERVER_SIDE,
            optionChangedBecauseOfSearch: false,
            selectedPatientForAttachmentTreeModal: null,
            entitiesIdArray: null
        }
    },
    computed: {
        coreEntityCode() {
            return COREENTITY_VALUES.SAMPLE.code
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        sideBarFilterModel() {
            if (!this.sideBarState || !this.sideBarState.selectedOption) {
                return {}
            }
            this.$nextTick(() => this.$refs.searcherModule?.focusInput())
            return this.sideBarState.selectedOption.filterModel
        },
        sideBar() {
            // changes when the sideBar is expanded or contracted
            return this.sideBarState.sideBar
        },
        rowHeight() {
            if (this.mode.value === 'compact') {
                return ROW_HEIGHT_COMPACT
            }
            return ROW_HEIGHT_NORMAL
        },
        sortModel() {
            return this.gridApi.getSortModel()
        },
        msgSelectedRows() {
            if (this.selectedRows.length) {
                const choice = this.selectedRows.length > 1 ? 2 : 1
                return `${this.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
            }
            return ''
        },
        selectedOption() {
            return this.sideBarState.selectedOption
        },
        uiComponent() {
            if (this.selectedOption) {
                return this.selectedOption.uicomponent
            }
            return null
        },
        displayedColumns() {
            return this.columnApi.getAllDisplayedColumns()
        },
        _showDiscardButton() {
            return this.selectedOption.task.showDiscardButton && this.$_canDiscardSample()
        },
        _disabledDiscardButton() {
            return !this.selectedRows.length
        },
        _showUndoDiscardButton() {
            return this.selectedOption.task.showUndoDiscardButton && this.$_canDiscardSample()
        },
        _disabledUndoDiscardButton() {
            return !this.selectedRows.length
        },
        componentId() {
            return this.selectedOption?.id?.toString()
        },
        gridOptions() {
            return {
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getWasteSampleContextMenu,
                getRowHeight: () => this.rowHeight,
                rowModelType: this.rowModel,
                autoGroupColumnDef: { headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true// remove number in Group Column
                    }
                }
            }
        }
    },
    watch: {
        sideBar() {
            if (this.gridApi) {
                this.gridApi.sizeColumnsToFit()
            }
        },
        selectedOption() {
            if (this.optionChangedBecauseOfSearch) {
                this.optionChangedBecauseOfSearch = false
                return
            }
            this.rowModel = AgGridRowModelType.SERVER_SIDE
            this.searchData = []
        }
    },
    async created() {
        try {
            await sideBarStore.loadCustomOptions()
        } catch (e) {
            this.showErrorModalGlobal()
        }
    },
    mounted() {
        // TODO: Revisar comportamiento de candado con esta opción
        if (this.$route.params.sidebarOption) {
            const option = sideBarStore.findOptionById(this.$route.params.sidebarOption)
            sideBarStore.setSelectedOption(option)
        } else {
            sideBarStore.selectDefaultOption()
        }
        if (this.$route.params.filterModel) {
            this.initFilterModel = this.$route.params.filterModel
        }
    },
    methods: {
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        async _getSidebarOptionsThatContainElement(sampleLabel) {
            const sidebarOptions = [
                sideBarStore.findOptionById('wasteToday'),
                sideBarStore.findOptionById('wasteLastWeek'),
                sideBarStore.findOptionById('wasteLastMonth'),
                sideBarStore.findOptionById('preserved'),
                sideBarStore.findOptionById('toBeDiscarded'),
                sideBarStore.findOptionById('toBeUnarchivedForDisposal')
            ]

            const searcherFilterModel = {
                samLabel: GridUtils.getTextFilterModel('equals', sampleLabel)
            }

            const searches =
                sidebarOptions.map(async (sidebarOption) => {
                    const filterModel = {
                        ...sidebarOption.filterModel,
                        ...searcherFilterModel
                    }

                    const foundElements =
                        await wasteSampleService.paginator(
                            filterModel,
                            { page: 0 },
                            this.laboratoryId,
                            sidebarOption.task.url)

                    return {
                        sidebarOption,
                        foundElements: foundElements?.content ?? []
                    }
                })

            const solvedSearches = await Promise.all(searches)
            return solvedSearches.filter(search => search.foundElements.length)
        },
        _getSidebarOptionFromSelectedSample(searches, sample) {
            if (searches?.length) {
                for (const search of searches) {
                    for (const foundElement of search.foundElements) {
                        if (foundElement.samId === sample.samId) {
                            return search.sidebarOption
                        }
                    }
                }
            }
            return null
        },
        async onSampleSearch(searchInformation) {
            const searches =
                await this.$_toCallUnique(
                    this._getSidebarOptionsThatContainElement(searchInformation.search))

            if (searches?.length) {
                let entitySelected = searches[0].foundElements[0]
                if (searches.length > 1 || searches[0].foundElements.length > 1) {
                    // get all elements from all searches
                    const foundElements = searches.flatMap(search => search.foundElements)

                    // remove duplicated elements
                    const foundElementsWithoutDuplicates = foundElements.filter((currentElement, currentIndex, foundElementsReference) =>
                        currentIndex === foundElementsReference.findIndex(foundElement => (
                            foundElement.samId === currentElement.samId
                        )))
                    if (foundElementsWithoutDuplicates.length > 1) {
                        const foundEntities = foundElementsWithoutDuplicates.map(foundElement => ({
                            entityType: IdentifierTypeScope.SAMPLE,
                            entityValue: {
                                samId: foundElement.samId,
                                sampleLabel: foundElement.samLabel,
                                caseStudy: {
                                    casenumber: foundElement.caseStudy.casCasenumber,
                                    laboratory: {
                                        labName: foundElement.caseStudy.laboratory.labName
                                    },
                                    patient: foundElement.caseStudy.patient
                                },
                                sampleType: foundElement.sampleType,
                                topographicCode: {
                                    description: foundElement.topographicCodeDescription
                                }
                            },
                            laboratoryId: foundElement.caseStudy.laboratory?.labId
                        }))
                        const entityValueSelected =
                          await this.$_showMultiEntitySelection(
                              searchInformation.search,
                              foundEntities)

                        entitySelected =
                          foundElements.find(foundElement => foundElement.samId === entityValueSelected.samId)
                    }
                }

                if (!entitySelected) {
                    return
                }

                const elementAlreadyAdded = this.searchData.find(item => item.samId === entitySelected.samId)
                if (elementAlreadyAdded) {
                    this.$_showWarningModal(this.$tc('component.searcher.error.elementAlreadyAdded'))
                    return
                }

                const sidebarOption = this._getSidebarOptionFromSelectedSample(searches, entitySelected)
                const previousNodes = this.gridApi.getRenderedNodes()
                if (sidebarOption !== this.selectedOption) {
                    if (this.searchData.length >= 1) {
                        this.$refs.optionChangeConfirmationModal.ask(() => {
                            // on ok callback
                            sideBarStore.setSelectedOption(sidebarOption)
                            this.optionChangedBecauseOfSearch = true
                            this.searchData = [entitySelected]
                            this.gridApi.setRowData(this.searchData)

                            this.$_keepSelectionGridWhenSearch(
                                this.gridApi,
                                entitySelected.samId,
                                previousNodes,
                                'samId')
                        })
                        return
                    } else {
                        this.optionChangedBecauseOfSearch = true
                        sideBarStore.setSelectedOption(sidebarOption)
                    }
                }
                this.searchData.push(entitySelected)

                if (this.rowModel === AgGridRowModelType.SERVER_SIDE) {
                    this.rowModel = AgGridRowModelType.CLIENT_SIDE
                } else {
                    this.gridApi.setRowData(this.searchData)
                }

                this.$_keepSelectionGridWhenSearch(
                    this.gridApi,
                    entitySelected.samId,
                    previousNodes,
                    'samId')
            } else {
                this.$_showErrorModal(this.$tc('component.searcher.error.noSample'))
            }
        },

        getColumnDefs() {
            switch (this.selectedOption.task.code) {
            case WASTE_TASK.TO_BE_DISCARDED.code:
                return wasteSampleGridToBeDiscardedColumnDefs()
            case WASTE_TASK.TO_BE_UNARCHIVED_FOR_DISPOSAL.code:
                return wasteSampleGridToBeUnarchivedForDisposalColumnDefs()
            case WASTE_TASK.PRESERVED.code:
                return wasteSampleGridPreservedColumnDefs()
            case WASTE_TASK.DISCARDED.code:
                return wasteSampleGridDiscardedColumnDefs()
            }
            return []
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        getRowData() {
            if (!this.gridApi) {
                return
            }
            if (this.initFilterModel) {
                this.gridApi.setFilterModel(this.initFilterModel)
                this.initFilterModel = null
                return
            }
            if (this.sideBarFilterModel) {
                this.applySideBarFilterModel()
            }
            this.gridApi.setServerSideDatasource(
                new PaginatorLaboratoryDynamicDatasource(
                    wasteSampleService,
                    this.laboratoryId,
                    this.selectedOption.task.url))
        },
        getWasteSampleContextMenu(params) {
            return [
                {
                    name: this.$tc('waste.grid.contextMenu.disposeOfSample'),
                    cssClasses: this._showDiscardButton && this.$_canDisposeSample(params?.node.data) ? '' : ['not-show'],
                    action: () => this.disposeOfSamples([params?.node.data])
                },
                {
                    name: this.$tc('waste.grid.contextMenu.undoDisposeOfSample'),
                    cssClasses: this._showUndoDiscardButton && this.$_canUndoDisposedSample(params?.node.data) ? '' : ['not-show'],
                    action: () => this.undoDisposeOfSamples([params?.node.data])
                },
                // TODO: Front development required for next action
                // {
                //    name: this.$tc('waste.grid.contextMenu.goToDisposal')
                // },
                {
                    name: this.$t('registry.caseGrid.contextMenu.viewIncidents'),
                    action: () => this.onIncidentsClicked(params?.node.data),
                    cssClasses: this.selectedRows.length > 1 ? ['not-show'] : ''
                },
                {
                    name: this.$t('registry.caseGrid.contextMenu.registerIncident'),
                    action: () => this.openIncidentFormModal(params?.node.data),
                    cssClasses: this.selectedRows.length === 1 && !!params.node.data.samId && this.$_canCreateIncident() && this.$_canBeIncidentCreatedToSample(params.node.data) ? '' : ['not-show']
                },
                {
                    name: this.$t('laboratory.grid.contextMenu.viewAttach'),
                    action: () => this.showAttachmentModal(params?.node.data),
                    cssClasses: this.selectedRows.length > 1 ? ['not-show'] : ''
                },
                {
                    name: this.$t('laboratory.grid.contextMenu.addAttach'),
                    action: () => this.showAddFileModal(params?.node.data),
                    cssClasses: this.selectedRows.length > 1 || !this.$_canAttachDocumentToSample() || !this.$_canBeDocumentAttachedToSample(params.node.data) ? ['not-show'] : ''
                },
                {
                    name: this.$t('global.showInGlobalLocator'),
                    action: () => this.showInGlobalLocator(params.node.data.samLabel),
                    cssClasses: this.selectedRows.length > 1 ? ['not-show'] : ''
                }
            ]
        },
        showAddFileModal(params) {
            this.entitiesIdArray = [this.laboratoryId, params.caseStudy.casId, params.samId]
            this.$refs.addFileModalWrapper.show()
        },
        showAttachmentModal(params) {
            this.selectedPatientForAttachmentTreeModal = { patId: params.caseStudy.patientId }
            this.$nextTick(() => {
                this.$refs.attachmentTreeModal.showSample(params.samId)
            })
        },
        openIncidentFormModal(sample) {
            const entitiesIds = {
                casId: sample.caseStudy.casId,
                samId: sample.samId
            }
            this.$nextTick(() => {
                this.$refs.incidentFormModal.show([null, entitiesIds])
            })
        },
        onIncidentsClicked(sample) {
            let defaultOption = {
                coreEntity: this.coreEntityCode,
                casId: sample.caseStudy.casId,
                samId: sample.samId
            }
            const patientId = sample.caseStudy.patientId
            this.$refs.incidentModalTreeWrapper.show([defaultOption, patientId])
        },
        async disposeOfSamples(samples) {
            if (!Array.isArray(samples) || !samples.length) {
                return
            }
            const [err, failedSamples] = await this.toCall(wasteSampleService.disposeOfSamples(this.laboratoryId, samples))
            if (err) {
                this.showErrorModalGlobal()
            } else if (failedSamples.data.length > 0) {
                // TODO: Check that the type of error is the right one with a localized message, if not, basic message should be sent
                this.showErrorModalGlobal(failedSamples.data[0].errorMessage)
            } else {
                this.showToastSuccessGlobal(this.$t('waste.modal.disposalSuccessful'))
            }
            this.rowModel = AgGridRowModelType.SERVER_SIDE
            this.reloadRowData()
            this.$nextTick(() => {
                this.$refs.searcherModule.focusInput()
            })
        },
        async undoDisposeOfSamples(samples) {
            const [err] = await this.toCall(wasteSampleService.undoDisposeOfSamples(this.laboratoryId, samples))
            if (err) {
                this.showErrorModalGlobal(err.response.data.code === this.$apiExceptions.SAMPLE_UNDO_DISPOSAL_ERROR ? err.response.data.message : this.$t('component.modalError.title'))
            } else {
                this.showToastSuccessGlobal(this.$t('waste.modal.undoDisposalSuccessful'))
            }
            this.reloadRowData()
            this.$nextTick(() => {
                this.$refs.searcherModule.focusInput()
            })
        },
        onGridReady(api) {
            this.gridApi = api.api
            this.columnApi = api.columnApi

            this.gridApi.setColumnDefs(this.getColumnDefs())
            if (this.rowModel === AgGridRowModelType.SERVER_SIDE) {
                this.getRowData()
            } else {
                this.gridApi.setRowData(this.searchData)
                this.gridApi.selectAll()
            }
        },
        reloadRowData() {
            this.rowModel = AgGridRowModelType.SERVER_SIDE
            this.searchData = []
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(
                new PaginatorLaboratoryDynamicDatasource(
                    wasteSampleService,
                    this.laboratoryId,
                    this.selectedOption.task.url))
            this.$emit('reload-sidebar-summary')
        },
        applySideBarFilterModel() {
            if (this.gridApi) {
                this.gridApi.deselectAll()
                this.gridApi.setFilterModel(this.sideBarFilterModel)
            }
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            GridUtils.changeRowHeightGrid(this.gridApi, selectedOption.size)
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        cleanFilters() {
            this.applySideBarFilterModel()
        },

        _getSelectedEntityIds() {
            return this.selectedRows.map(selectedRow =>
                ({
                    caseId: selectedRow.caseStudy.casId,
                    sampleId: selectedRow.samId
                }))
        },
        exportReport(exportType) {
            const columns = this.$_getColumnsToExport(this.gridApi, this.columnApi)
            if (columns && this.gridApi) {
                return wasteSampleService.exportTo(
                    this.laboratoryId,
                    this.$tc(this.sideBarState.selectedOption.label),
                    this.encoder.parseBooleans(this.gridApi.getFilterModel()),
                    GridUtils.getSortValue(this.sortModel),
                    columns,
                    this._getSelectedEntityIds(),
                    this.selectedOption.task.url,
                    exportType)
            }
            return null
        },

        onSideBarClickAddFilter() {
            this.$refs.addFilterModal.show()
        },
        async onAddFilterOk(filter) {
            const params = {
                label: filter.label,
                filterModel: this.gridApi.getFilterModel(),
                task: this.selectedOption.task,
                uicomponent: this.selectedOption.uicomponent
            }
            await customFilterResource.create(this.sideBarState.componentCode, params)
            await sideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterAdded'))
        },
        async onSideBarClickUpdateFilter(option) {
            if (isEmpty(this.gridApi.getFilterModel())) {
                this.saveNoFilter()
                return
            }
            const params = {
                filterModel: this.gridApi.getFilterModel(),
                task: this.selectedOption.task,
                uicomponent: this.selectedOption.uicomponent
            }
            const [err] = await this.toCall(sideBarStore.updateCustomOption(option, params))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            await sideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterUpdated'))
        }
    }
}
</script>

<style>
.waste-module .waste-sample-grid {
  height: 100%;
}
</style>
